// Copyright 2016 Sorama B.V.

import { inject } from "aurelia-framework";
import { CaptureDevice } from "datatypes/SoramaLocalApi";
import { MeasurementDeviceRepository } from "repositories/IndexedDbRepositories";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { RegistrableMeasurementDevice } from "services/Hardware/Datatypes/RegistrableMeasurementDevice";
import { IMeasurementDeviceManagement } from "services/Hardware/Management/IMeasurementDeviceManagement";
import { ManagementHelperFunctions } from "services/Hardware/Management/ManagementHelperFunctions";

@inject(MeasurementDeviceRepository, "RemoteFrontEndManagement")
export class CachedMeasurementDeviceManagement implements IMeasurementDeviceManagement {
    private database: IDBDatabase;
    private remoteFrontEndManagement: IMeasurementDeviceManagement;

    private measurementDeviceRepository: MeasurementDeviceRepository;

    private updatedDeviceIds: string[] = [];

    constructor(
        measurementDeviceRepository: MeasurementDeviceRepository,
        remoteFrontEndManagement: IMeasurementDeviceManagement
    ) {
        this.measurementDeviceRepository = measurementDeviceRepository;
        this.remoteFrontEndManagement = remoteFrontEndManagement;
    }

    public getRegisteredMeasurementDevices(measurementDevices: MeasurementDevice[]): Promise<MeasurementDevice[]> {
        const promises: Promise<MeasurementDevice>[] = [];

        for (const device of measurementDevices) {
            promises.push(
                this.measurementDeviceRepository.getById(device.id).catch((x) => {
                    // Not found, ignore entry
                    return undefined;
                })
            );
        }

        return Promise.all<MeasurementDevice>(promises).then((cachedMeasurementDevices: MeasurementDevice[]) => {
            // In the background, update FrontEnds
            this.remoteFrontEndManagement
                .getRegisteredMeasurementDevices(
                    measurementDevices.filter((device) => this.updatedDeviceIds.findIndex((x) => device.id == x) == -1)
                )
                .then((remoteMeasurementDevices: MeasurementDevice[]) => {
                    // Cache newly fetched devices
                    for (const measurementDevice of remoteMeasurementDevices) {
                        this.updatedDeviceIds.push(measurementDevice.id);

                        const cachedFrontEnd =
                            RegistrableMeasurementDevice.createRegistrableCachedDevice(measurementDevice);
                        this.registerMeasurementDevice(cachedFrontEnd).catch(); // Not being able to cache is not really an error
                    }
                })
                .catch((e) => {
                    //do nothing when this fails
                });

            return this.checkHasDeviceSameProperties(cachedMeasurementDevices, measurementDevices);
        });
    }

    public registerMeasurementDevice(
        registrableMeasurementDevice: RegistrableMeasurementDevice
    ): Promise<MeasurementDevice> {
        return this.measurementDeviceRepository
            .put(registrableMeasurementDevice.originalDevice)
            .then(() => registrableMeasurementDevice.originalDevice);
    }

    public getCachedMeasurementDevices(): Promise<MeasurementDevice[]> {
        return this.measurementDeviceRepository.getAll();
    }

    private checkHasDeviceSameProperties(
        cachedDevices: MeasurementDevice[],
        requestedDevices: MeasurementDevice[]
    ): MeasurementDevice[] {
        const correctHardware: MeasurementDevice[] = [];

        for (const cachedDevice of cachedDevices) {
            // Skip undefined devices
            if (!cachedDevice) {
                continue;
            }

            const requestedDevice = requestedDevices.find((x) => x.id === cachedDevice.id);

            if (!requestedDevice) {
                continue;
            }

            const resultingDevice = MeasurementDevice.copy(requestedDevice);

            // Check frontends
            resultingDevice.frontEnd.name = cachedDevice.frontEnd.name;

            // Check capture devices.
            const captureDevices: CaptureDevice[] = [];
            const connectedCaptureDevices =
                ManagementHelperFunctions.filterCaptureDevicesForMeasurementDevice(requestedDevice);
            for (const captureDevice of cachedDevice.captureDevices) {
                const dev = connectedCaptureDevices.find(
                    (y) => y.productId === captureDevice.productId && y.vendorId == captureDevice.vendorId
                );

                if (dev) {
                    captureDevice.uri = dev.uri;
                    captureDevice.id = dev.id;
                    captureDevices.push(captureDevice);
                } else {
                    // Make sure that connection info is removed for not connected capture devices
                    captureDevice.uri = "";
                    captureDevices.push(captureDevice);
                }
            }
            resultingDevice.captureDevices = captureDevices;

            // Set Microphone Array Rack ids
            resultingDevice.microphoneArrayRackIds = cachedDevice.microphoneArrayRackIds;

            // Double check connected hardware ids
            let isCorrect = true;
            for (const connector of cachedDevice.frontEnd.frontEndConnectors) {
                const requestedConnector = requestedDevice.frontEnd.frontEndConnectors.find(
                    (x) => x.id == connector.id
                );
                if (requestedConnector) {
                    for (const micArray of connector.microphoneArrayIds) {
                        if (!requestedConnector.microphoneArrayIds.find((x) => micArray == x)) {
                            isCorrect = false;
                            break;
                        }
                    }
                } else {
                    isCorrect = false;
                    break;
                }
            }

            if (isCorrect) {
                correctHardware.push(resultingDevice);
            }
        }

        return correctHardware;
    }
}
